// Vendor-neutral PARs, and MOQs that belong to a vendor.
//
// Two rules this module exists to enforce:
//
//   A PAR is a property of the ITEM, not of a vendor. You may TYPE it as
//   "2 Costco tubs" because that is how you think about the shelf, but what
//   gets stored and compared is 10 gal. Switching the preferred vendor must
//   never move the PAR.
//
//   A MOQ is a property of a VENDOR PRODUCT. "2 cases" is meaningless until we
//   know whose case. A single item-level MOQ cannot be right for an item bought
//   four ways, so the legacy column is a last-resort fallback, not the answer.
//
// The dangerous case both rules guard against is the ambiguous container word.
// "2 cases" looks like a quantity and is not one: a Costco case and a Sysco
// case hold different amounts. Rather than pick one, normalizePar refuses and
// asks which product is meant.
//
// Pure ESM. No Deno APIs, no I/O.

import { normalizeUnit, convertToBase } from "../menu-recipe-costing/units.mjs";
import { interpretPackage } from "./packageLadder.mjs";
import { countUnitAnchor, sameCountUnitName } from "./packText.mjs";
import { vendorIsRetired } from "./vendorState.mjs";

const round6 = (n) => Math.round(n * 1e6) / 1e6;

// Number(null), Number('') and Number([]) are all 0, so a missing value would
// otherwise pass as a legitimate zero. Reject the empties before coercing.
const finiteNonNegative = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const positive = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const text = (v) => (v == null ? "" : String(v).trim());

const isMeasurable = (norm) =>
  !!norm && (norm.kind === "mass" || norm.kind === "volume" || norm.kind === "count");

/** Why a PAR could not be normalized. */
export const PAR_STATUS = Object.freeze({
  VALID: "valid",
  REQUIRES_OPTION: "requires_option",
  UNRESOLVED: "unresolved",
  EMPTY: "empty",
});

export const PAR_REASON_TEXT = Object.freeze({
  requires_option:
    "That unit names a container, not an amount. Choose which vendor product it is so the PAR can be stored as a real quantity.",
  unknown_unit: "That unit is not one we can convert into the item's count unit.",
  incompatible_units: "That unit cannot be converted into the item's count unit.",
  unresolved_package: "That vendor package has no confirmed size yet.",
  bad_value: "The PAR must be a number of zero or more.",
  unknown_count_unit: "The item's count unit is not one we can convert.",
});

/**
 * Normalizes a PAR into the item's count unit.
 *
 * @param {object} args
 * @param {number} args.value               what the user typed
 * @param {string} [args.unit]              the unit they typed it in; defaults to the count unit
 * @param {*}      [args.purchaseOptionId]  set when the unit is a vendor package
 * @param {string} args.countUnit           the item's canonical count unit
 * @param {Array}  [args.options]           purchase_options rows for the item
 * @returns {{ status:string, normalized:number|null, normalizedUnit:string|null,
 *             display:{ value:number|null, unit:string|null, optionId:* },
 *             reason:string|null, reasonText:string|null,
 *             snapshot:object|null, warnings:string[] }}
 */
export function normalizePar({ value, unit, purchaseOptionId, countUnit, options } = {}) {
  const warnings = [];
  const display = { value: finiteNonNegative(value), unit: text(unit) || null, optionId: purchaseOptionId ?? null };

  const fail = (status, reason) => ({
    status,
    normalized: null,
    normalizedUnit: null,
    display,
    reason,
    reasonText: PAR_REASON_TEXT[reason] || null,
    snapshot: null,
    warnings,
  });

  const qty = finiteNonNegative(value);
  if (qty === null) return fail(PAR_STATUS.EMPTY, "bad_value");

  // The item's own shelf unit. A wrapper or piece noun (bottle, case, bag)
  // is a real unit to hold a PAR in — "PAR 3 bottles" on a bottle-counted
  // item is three bottles — it simply cannot be CONVERTED into, which the
  // measure branch below still refuses. Requiring a convertible unit here is
  // why a bar's PARs resolved on the count screen and vanished on the server.
  const countAnchor = countUnitAnchor(countUnit);
  if (!countAnchor.anchorable) return fail(PAR_STATUS.UNRESOLVED, "unknown_count_unit");
  const countNorm = normalizeUnit(countUnit);

  // A wrapper the engine does not name at all ("bib") still anchors a count;
  // countUnitAnchor supplies the canonical word so the stored unit is never
  // null on a shelf that is plainly countable.
  const countUnitKey = countNorm.key || countAnchor.key;

  const ok = (normalized, snapshot) => ({
    status: PAR_STATUS.VALID,
    normalized: round6(normalized),
    normalizedUnit: countUnitKey,
    display,
    reason: null,
    reasonText: null,
    snapshot,
    warnings,
  });

  // A PAR expressed in a vendor package: "2 Costco tubs".
  if (purchaseOptionId != null && purchaseOptionId !== "") {
    const option = (options || []).find((o) => o && String(o.id) === String(purchaseOptionId));
    if (!option) return fail(PAR_STATUS.UNRESOLVED, "unresolved_package");
    const interp = interpretPackage(option, countUnit);
    const perPackage = positive(interp.quantity_in_item_count_unit);
    if (perPackage === null) return fail(PAR_STATUS.UNRESOLVED, "unresolved_package");
    warnings.push(...(interp.warnings || []));
    return ok(qty * perPackage, {
      entered_as: "purchase_option",
      purchase_option_id: option.id,
      quantity_per_package: perPackage,
      package_label: interp.order_unit || null,
      package_interpretation_source: interp.source,
      count_unit: countUnitKey,
    });
  }

  // No unit given means the PAR is already in the item's own unit.
  const enteredUnit = text(unit);
  if (!enteredUnit) return ok(qty, { entered_as: "count_unit", count_unit: countUnitKey });

  const enteredNorm = normalizeUnit(enteredUnit);

  // The PAR is already in the item's own unit, said out loud. Checked by NAME
  // and before the container refusal below: "3 bottles" on a bottle-counted
  // item is a stocking level, not the ambiguous-wrapper trap — the wrapper is
  // the shelf, and the shelf needs no product named to be counted on.
  if (sameCountUnitName(enteredUnit, countUnit)) {
    return ok(qty, { entered_as: "count_unit", count_unit: countUnitKey });
  }

  // "case", "tub", "box" — a DIFFERENT container from the one counted, with
  // no size. This is the trap: the number looks usable and is not. Refuse and
  // ask which product.
  if (enteredNorm.kind === "container") {
    return fail(PAR_STATUS.REQUIRES_OPTION, "requires_option");
  }
  if (!isMeasurable(enteredNorm)) return fail(PAR_STATUS.UNRESOLVED, "unknown_unit");

  // A measure cannot be poured into a wrapper. "2.5 gal" on a bottle-counted
  // item needs a package to bridge through, and inventing the bridge here is
  // exactly the fabricated conversion the domain model forbids.
  if (countAnchor.kind === "container") {
    return fail(PAR_STATUS.UNRESOLVED, "incompatible_units");
  }

  const conv = convertToBase(qty, enteredUnit, countNorm.base, {});
  warnings.push(...(conv.warnings || []));
  if (!conv.ok) return fail(PAR_STATUS.UNRESOLVED, "incompatible_units");

  return ok(conv.qty / countNorm.factor, {
    entered_as: "measure",
    entered_unit: enteredNorm.key,
    count_unit: countNorm.key,
  });
}

/**
 * The PAR to compare against on-hand, for a row that may predate normalization.
 *
 * A legacy row stores par_level + par_unit with no guarantee they convert. If
 * par_unit already IS the count unit — overwhelmingly the common case — the
 * number is used as-is. Otherwise it is converted, and if it cannot be, the
 * PAR is reported unresolved rather than subtracted as though the units matched.
 */
export function resolveRowPar(row, { countUnit, options } = {}) {
  if (!row) return normalizePar({ value: null, countUnit });

  // A previously normalized value wins — it was computed when the package was
  // confirmed and must not drift if the parser changes later.
  const stored = finiteNonNegative(row.par_normalized_quantity);
  if (stored !== null) {
    const countNorm = normalizeUnit(row.par_normalized_unit || countUnit);
    return {
      status: PAR_STATUS.VALID,
      normalized: round6(stored),
      normalizedUnit: countNorm.key || null,
      display: {
        value: finiteNonNegative(row.par_display_quantity) ?? finiteNonNegative(row.par_level),
        unit: text(row.par_display_unit) || text(row.par_unit) || null,
        optionId: row.par_purchase_option_id ?? null,
      },
      reason: null,
      reasonText: null,
      snapshot: row.par_conversion_snapshot || null,
      warnings: [],
    };
  }

  // expected_quantity is the pre-par_level PAR column; rows old enough to
  // carry only it still state a PAR, under the same unit rules.
  return normalizePar({
    value: row.par_level ?? row.expected_quantity,
    unit: row.par_unit || countUnit,
    purchaseOptionId: row.par_purchase_option_id ?? null,
    countUnit,
    options,
  });
}

/* ── MOQ ──────────────────────────────────────────────────────────────────── */

export const MOQ_SOURCES = Object.freeze({
  SELECTED_OPTION: "selected_option",
  PREFERRED_OPTION: "preferred_option",
  ONLY_OPTION: "only_option",
  LEGACY_ROW: "legacy_row",
  DEFAULT: "default",
});

/**
 * The minimum number of packages to order on this line, and where that number
 * came from.
 *
 * Precedence, highest first:
 *   1. the selected purchase option's moq_packages
 *   2. the preferred option's, or the only option's
 *   3. the legacy row/item MOQ — single-vendor compatibility only
 *   4. one package
 *
 * The legacy value is deliberately last and is NOT used when the line has a
 * resolved vendor product: "2" on the item row was entered when the item had
 * one vendor, and applying it to a different vendor's case is how you order
 * two pallets by accident.
 *
 * @returns {{ packages:number, source:string, optionId:*, variesByVendor:boolean }}
 */
export function resolveMoqPackages({ selectedOption, options, legacyMoq } = {}) {
  // A retired vendor's MOQ must never enter a new-order calculation — its
  // package may still describe stock on the shelf, but its minimum is not a
  // constraint on anyone anymore.
  const active = (options || []).filter(
    (o) => o && o.status === "active" && !vendorIsRetired(o),
  );
  const distinctMoqs = new Set(
    active.map((o) => positive(o.moq_packages)).filter((m) => m !== null).map((m) => Math.ceil(m)),
  );
  const varies = active.length >= 2 && distinctMoqs.size >= 2;

  const fromOption = (option, source) => {
    if (vendorIsRetired(option)) return null;
    const moq = positive(option?.moq_packages);
    if (moq === null) return null;
    return { packages: Math.ceil(moq), source, optionId: option.id ?? null, variesByVendor: varies };
  };

  const selected = fromOption(selectedOption, MOQ_SOURCES.SELECTED_OPTION);
  if (selected) return selected;

  // Only fall further back when no option was selected at all. A selected
  // option with no MOQ means "no minimum", not "use somebody else's".
  if (!selectedOption) {
    const preferred = active.find((o) => o.is_preferred === true);
    const fromPreferred = fromOption(preferred, MOQ_SOURCES.PREFERRED_OPTION);
    if (fromPreferred) return fromPreferred;

    if (active.length === 1) {
      const only = fromOption(active[0], MOQ_SOURCES.ONLY_OPTION);
      if (only) return only;
    }

    const legacy = positive(legacyMoq);
    if (legacy !== null) {
      return { packages: Math.ceil(legacy), source: MOQ_SOURCES.LEGACY_ROW, optionId: null, variesByVendor: varies };
    }
  }

  return {
    packages: 1,
    source: MOQ_SOURCES.DEFAULT,
    optionId: selectedOption?.id ?? null,
    variesByVendor: varies,
  };
}

/* ── Need → packages ──────────────────────────────────────────────────────── */

/**
 * How many packages cover a need, in the vendor's legal steps, and what
 * actually arrives.
 *
 *   raw      = need / qtyPerPackage
 *   stepped  = ceil(raw / orderIncrement) × orderIncrement
 *   packages = max(MOQ, stepped)
 *
 * MOQ, order increment, and fractional COUNTING are three different facts:
 * the MOQ is the least the vendor will sell, the increment is the steps they
 * sell in, and whether a partial package may sit on a shelf is a property of
 * the package's physical structure that never changes what may be ORDERED.
 *
 * `orderIncrement` unstated (null) means whole packages — an effective step
 * of 1 — except for cut-to-order vendors (`allowsFractional`), where it means
 * the vendor cuts whatever is asked and the quantity passes through exactly.
 * A STATED fractional increment steps even a cut-to-order option: a 6.1 lb
 * need at a 0.25 lb step is a 6.25 lb order, never 6.1.
 *
 * The sibling in purchaseConversion.mjs answers the same question for the
 * price-conversion path and returns 0 packages when the package size is
 * unknown. That is wrong for ordering: "we do not know how big a case is"
 * would silently become "order nothing". Here an unknown package size returns
 * the minimum with a reason attached and a null overage, so the caller has to
 * show the gap rather than skip the line.
 *
 * `allowsFractional` defaults off, and it also removes the implicit minimum
 * of one package — clamping a 0.7 lb order up to a whole package would defeat
 * the point.
 *
 * @returns {{ packages:number|null, quantityReceived:number|null,
 *             overage:number|null, reason:string|null }}
 */
export function orderPackagesForNeed({
  need,
  qtyPerPackage,
  moqPackages,
  allowsFractional = false,
  orderIncrement,
} = {}) {
  const needQty = finiteNonNegative(need);
  if (needQty === null) {
    return { packages: null, quantityReceived: null, overage: null, reason: "unknown_need" };
  }

  const increment = positive(orderIncrement) ?? (allowsFractional ? null : 1);
  // 1e-9 absorbs float noise before the ceiling: 3 / 1.5 must be 2 steps, not
  // 3, and 0.3 at a 0.1 step must be 3 steps, not 4. round6 after keeps the
  // result on the same 6dp grid every stored quantity uses.
  const snapUp = (value) => round6(Math.ceil(value / increment - 1e-9) * increment);

  const stated = positive(moqPackages);
  const moq =
    increment === null
      ? stated !== null
        ? Math.ceil(stated)
        : 0
      : stated !== null
        ? snapUp(stated)
        : allowsFractional
          ? 0
          : snapUp(1);

  if (needQty <= 0) return { packages: 0, quantityReceived: 0, overage: 0, reason: null };

  const per = positive(qtyPerPackage);
  if (per === null) {
    return {
      packages: Math.max(1, moq),
      quantityReceived: null,
      overage: null,
      reason: "unresolved_package",
    };
  }

  const exact = needQty / per;
  const packages = increment === null ? Math.max(moq, round6(exact)) : Math.max(moq, snapUp(exact));
  const quantityReceived = round6(packages * per);
  return { packages, quantityReceived, overage: round6(quantityReceived - needQty), reason: null };
}

/* ── Typed order quantities ───────────────────────────────────────────────── */

/**
 * The vendor's legal ordering grid for one purchase option, stated once so a
 * typed package quantity (draft PO edit, revision, direct order) is judged by
 * the SAME rules orderPackagesForNeed applies to a computed need.
 *
 *   increment — the legal step, in packages. Unstated means whole packages,
 *               except cut-to-order (allowsFractional), where it means the
 *               vendor cuts whatever is asked (null = no grid).
 *   moq       — the least the vendor sells, snapped up to the increment grid.
 *               An unstated MOQ is one whole step, or zero for cut-to-order.
 *
 * @returns {{ increment:number|null, moq:number }}
 */
export function orderRuleGrid({ moqPackages, orderIncrement, allowsFractional = false } = {}) {
  const increment = positive(orderIncrement) ?? (allowsFractional ? null : 1);
  const snapUp = (value) => round6(Math.ceil(value / increment - 1e-9) * increment);
  const stated = positive(moqPackages);
  const moq =
    increment === null
      ? stated !== null
        ? Math.ceil(stated)
        : 0
      : stated !== null
        ? snapUp(stated)
        : allowsFractional
          ? 0
          : snapUp(1);
  return { increment, moq };
}

/**
 * Is a typed package quantity one the vendor will accept?
 *
 * Zero is deliberately NOT legal here: a zero-quantity line is "remove the
 * line", not an order, and callers own that message. Below-MOQ and off-grid
 * quantities name the violated rule so the caller can explain it.
 *
 * @returns {{ legal:boolean, reason:null|'no_quantity'|'below_moq'|'off_increment',
 *             moq:number, increment:number|null }}
 */
export function validateOrderPackages({ packages, moqPackages, orderIncrement, allowsFractional = false } = {}) {
  const { increment, moq } = orderRuleGrid({ moqPackages, orderIncrement, allowsFractional });
  const qty = positive(packages);
  if (qty === null) return { legal: false, reason: "no_quantity", moq, increment };
  if (moq > 0 && qty < moq - 1e-9) return { legal: false, reason: "below_moq", moq, increment };
  if (increment !== null) {
    const steps = qty / increment;
    if (Math.abs(steps - Math.round(steps)) > 1e-6) {
      return { legal: false, reason: "off_increment", moq, increment };
    }
  }
  return { legal: true, reason: null, moq, increment };
}

/**
 * The nearest legal quantity at or above a typed one — max(MOQ, snapped up to
 * the increment grid). Zero passes through (line suppression stays a caller
 * decision), and an already-legal quantity comes back unchanged.
 *
 * @returns {{ packages:number, adjusted:boolean, reason:null|'below_moq'|'off_increment' }}
 */
export function legalizeOrderPackages({ packages, moqPackages, orderIncrement, allowsFractional = false } = {}) {
  const qty = finiteNonNegative(packages);
  if (qty === null || qty <= 0) return { packages: qty ?? 0, adjusted: false, reason: null };
  const check = validateOrderPackages({ packages: qty, moqPackages, orderIncrement, allowsFractional });
  if (check.legal) return { packages: qty, adjusted: false, reason: null };
  const { increment, moq } = check;
  const stepped = increment === null ? round6(qty) : round6(Math.ceil(qty / increment - 1e-9) * increment);
  return { packages: round6(Math.max(moq, stepped)), adjusted: true, reason: check.reason };
}

/**
 * "MOQ varies by vendor" vs a concrete minimum, for display.
 */
export function describeMoq(resolution, option) {
  if (!resolution) return null;
  if (resolution.source === MOQ_SOURCES.DEFAULT && resolution.variesByVendor) {
    return "MOQ varies by vendor";
  }
  const unit = text(option?.purchase_unit) || "package";
  const plural = resolution.packages === 1 ? unit : `${unit}s`;
  return `${resolution.packages} ${plural} minimum`;
}

/* ── Item replenishment target ────────────────────────────────────────────── */

/** Why an order quantity is what it is — the single decisive constraint. */
export const ORDER_DRIVERS = Object.freeze({
  NONE: "none",
  NEED: "need",
  ITEM_MINIMUM: "item_minimum",
  ITEM_INCREMENT: "item_increment",
  PACKAGE_ROUNDING: "package_rounding",
  VENDOR_MOQ: "vendor_moq",
  VENDOR_INCREMENT: "vendor_increment",
  UNRESOLVED: "unresolved",
});

/**
 * Raw need → the restaurant's own purchase target, before any vendor exists.
 *
 * The ITEM replenishment minimum is a FLOOR, never a multiple: a raw need of
 * 8 LB against a 5 LB minimum targets 8 LB, not 10. "Replenish only in
 * multiples of 5 LB" is a different fact — the ITEM replenishment increment —
 * and only it may step the target. Both are stated in the item's own count
 * unit; vendor packages, MOQs and order increments are applied AFTER this, in
 * packages (orderPackagesForNeed).
 *
 * Zero need orders nothing regardless of any minimum — a minimum floors a
 * real shortage, it does not invent one.
 *
 * @returns {{ target:number|null, driver:string, rawNeed:number|null }}
 */
export function computePurchaseTarget({ rawNeed, itemMinimum, itemIncrement } = {}) {
  const need = finiteNonNegative(rawNeed);
  if (need === null) return { target: null, driver: ORDER_DRIVERS.UNRESOLVED, rawNeed: null };
  if (need <= 0) return { target: 0, driver: ORDER_DRIVERS.NONE, rawNeed: 0 };

  let target = need;
  let driver = ORDER_DRIVERS.NEED;

  const minimum = positive(itemMinimum);
  if (minimum !== null && minimum > target) {
    target = minimum;
    driver = ORDER_DRIVERS.ITEM_MINIMUM;
  }

  const increment = positive(itemIncrement);
  if (increment !== null) {
    const stepped = round6(Math.ceil(target / increment - 1e-9) * increment);
    if (stepped > target) {
      target = stepped;
      driver = ORDER_DRIVERS.ITEM_INCREMENT;
    }
  }

  return { target: round6(target), driver, rawNeed: round6(need) };
}

/**
 * The whole ordering decision for one line, layer by layer, with the decisive
 * constraint named:
 *
 *   raw need → item minimum/increment → purchase target
 *   → vendor package → vendor increment → vendor MOQ → BUY
 *   → receive / extra vs need / extra vs target / cost.
 *
 * Raw need is preserved beside the buy; the reason the order grew is never
 * hidden. Pure arithmetic; the caller supplies qtyPerPackage already resolved
 * IN THE NEED UNIT (see packageBasis.resolveNeedToPackageBasis).
 *
 * @returns {{ rawNeed:number|null, target:number|null, targetDriver:string,
 *             packages:number|null, quantityReceived:number|null,
 *             overageVsNeed:number|null, overageVsTarget:number|null,
 *             cost:number|null, driver:string, reason:string|null }}
 */
export function computeOrderPlan({
  rawNeed,
  itemMinimum,
  itemIncrement,
  qtyPerPackage,
  moqPackages,
  allowsFractional = false,
  orderIncrement,
  packagePrice,
} = {}) {
  const targeting = computePurchaseTarget({ rawNeed, itemMinimum, itemIncrement });
  const empty = {
    rawNeed: targeting.rawNeed, target: targeting.target, targetDriver: targeting.driver,
    packages: null, quantityReceived: null, overageVsNeed: null, overageVsTarget: null,
    cost: null, driver: ORDER_DRIVERS.UNRESOLVED, reason: null,
  };
  if (targeting.target === null) return { ...empty, reason: "unknown_need" };
  if (targeting.target <= 0) {
    return { ...empty, packages: 0, quantityReceived: 0, overageVsNeed: 0, overageVsTarget: 0, cost: 0, driver: ORDER_DRIVERS.NONE };
  }

  const order = orderPackagesForNeed({
    need: targeting.target, qtyPerPackage, moqPackages, allowsFractional, orderIncrement,
  });
  if (order.reason) {
    return { ...empty, packages: order.packages, reason: order.reason };
  }

  const per = positive(qtyPerPackage);
  const exactPackages = targeting.target / per;
  const increment = positive(orderIncrement) ?? (allowsFractional ? null : 1);
  const stepped = increment === null
    ? round6(exactPackages)
    : round6(Math.ceil(exactPackages / increment - 1e-9) * increment);

  // The decisive constraint is the outermost layer that actually raised the
  // quantity: vendor MOQ > vendor increment / package rounding > item rules.
  let driver = targeting.driver;
  if (stepped > exactPackages + 1e-9) {
    driver = positive(orderIncrement) != null && positive(orderIncrement) !== 1
      ? ORDER_DRIVERS.VENDOR_INCREMENT
      : ORDER_DRIVERS.PACKAGE_ROUNDING;
  }
  if (order.packages > stepped + 1e-9) {
    driver = ORDER_DRIVERS.VENDOR_MOQ;
  }

  const price = positive(packagePrice);
  return {
    rawNeed: targeting.rawNeed,
    target: targeting.target,
    targetDriver: targeting.driver,
    packages: order.packages,
    quantityReceived: order.quantityReceived,
    overageVsNeed: round6(order.quantityReceived - targeting.rawNeed),
    overageVsTarget: round6(order.quantityReceived - targeting.target),
    cost: price != null ? Math.round(order.packages * price * 100) / 100 : null,
    driver,
    reason: null,
  };
}
